import {Injectable} from '@angular/core';
import {WorkingActions} from '../../../models/enums.model';
import {QuotationService} from '../../../services/quotation.service';
import {MatDialog} from '@angular/material';
import {WarrantyService, WarrantyTypes} from '../../../services/warranty.service';
import {WorkingModel} from '../../../models/working.model';
import {DossierModel} from '../../../models/dossier.model';
import {WorkingsService} from '../../../services/workings.service';
import {SelectCommoditiesService} from '@shared/service/select-commodities.service';
import {QuotationListInfoModel} from '../../../models/quotation-list-info.model';
import {AppConfigEnvironmentService} from '@shared/service/appconfig-environment.service';
import {AppConfigEnvironment} from '@shared/models/appconfig-environment.model';
import {CarBodyQuotationDetail, MechanicQuotationDetail} from '../../../models/quotation-details.model';
import {LoaderService} from '@shared/service/loader.service';
import {Observable, Subject} from 'rxjs';
import {TableHeaderActionService} from '@shared/components/actions/tableHeaderActionService';
import {ConsipFlowService} from '../../../services/consip-flow.service';
import {MessageManagerService} from '@shared/service/message-manager/message-manager.service';
import {SupervisorActions} from './user-actions/supervisor-actions';
import {AssistanceCenterActions} from './user-actions/assistance-center-actions';
import {CommonActions} from './user-actions/common-actions';
import {AuthorityActions} from './user-actions/authority-actions';
import {InvoiceModel} from '../../../models/invoice.model';
import {AlertService} from '../../../services/alert.service';
import {AuthorityChannelingService} from '../../../services/authority-channeling.service';
import {DossiersService} from '../../../services/dossiers.service';
import {DocumentsService} from '../../../services/documents.service';

/**
 *  In this service are handled all WorkingActions.
 *  To let it work correctly make sure to subscribe to all this Observables:
 *  - needRefreshDossierObservable
 *  - needRefreshQuotationsObservable
 *  - onEditedQuotationObservable
 *  and to set the parameters:
 *  - working: WorkingModel
 *  - dossier: DossierModel
 *  - allQuotations: QuotationListInfoModel;
 *  - showItemTableSelectionActions = false
 *  - tableSelectedItems: string[] = []
 *  - mechanicQuotationDetail: MechanicQuotationDetail
 *  - carBodyQuotationDetail: CarBodyQuotationDetail
 */
export interface QuotationEntry {
	warrantyType: WarrantyTypes;
	isForDealer: boolean;
	isNew: boolean;
	shippingDate: string;
	id: string;
	arrivedAt: string;
	isForConsip: boolean;
	isApproved?: boolean;
	dealerHandler?: boolean;
}

@Injectable()
export class WorkingManagerService extends TableHeaderActionService<WorkingActions> {

	onEditedQuotationObservable: Observable<any>;
	working: WorkingModel;
	dossier: DossierModel;
	allQuotations: QuotationListInfoModel;
	showItemTableSelectionActions: boolean = false;
	mechanicQuotationDetail: MechanicQuotationDetail;
	carBodyQuotationDetail: CarBodyQuotationDetail;
	selectedEntry: string[];
	assignManyParts: boolean;
	tableSelectedEntry: QuotationEntry[];
	needSendAdministrationPracticeObservable: Observable<boolean>;
	tableEntry: QuotationEntry[];
	invoiceList: InvoiceModel[];

	private readonly onEditedQuotationSubject: Subject<any>;
	private readonly needSendAdministrationPracticeSubject: Subject<boolean>;
	private readonly environment: AppConfigEnvironment;

	constructor(
		private appConfig: AppConfigEnvironmentService,
		private loaderService: LoaderService,
		private quotationService: QuotationService,
		private dialog: MatDialog,
		private messageManagerService: MessageManagerService,
		private workingsService: WorkingsService,
		private selectCommoditiesService: SelectCommoditiesService,
		private warrantyService: WarrantyService,
		private consipFlowService: ConsipFlowService,
		private alertService: AlertService,
		private authorityChannelingService: AuthorityChannelingService,
		private dossiersService: DossiersService,
		private documentService: DocumentsService
	) {
		super();
		this.tableSelectedEntry = [];
		this.environment = appConfig.Environment;

		this.onEditedQuotationSubject = new Subject<any>();
		this.onEditedQuotationObservable = this.onEditedQuotationSubject.asObservable();

		this.needSendAdministrationPracticeSubject = new Subject<boolean>();
		this.needSendAdministrationPracticeObservable = this.needSendAdministrationPracticeSubject.asObservable();

		this.actionsMap = new Map<WorkingActions, () => void>([

			[WorkingActions.APPROVE, () => AuthorityActions.approve(
					this.quotationService, this.loaderService, this.dossier.id, this.working.id, this.allQuotations, this.dossiersService.getDossierRefresher(), this.messageManagerService
				)
			],
			[WorkingActions.DECLINE, () => CommonActions.openDeclineDialog(
					this.dialog, this.dossier.id, this.working, this.allQuotations, this.dossiersService.getDossierRefresher(), WorkingActions.DECLINE
				)
			],
			[WorkingActions.SEND_QUOTATION, () => AssistanceCenterActions.sendQuotation(
					this.quotationService, this.messageManagerService, this.loaderService, this.dossiersService.getDossierRefresher(), this.dossier.id, this.working.id, this.allQuotations, this.working.type
				)
			],
			[WorkingActions.START_WORKING, () => CommonActions.openConfirmActionDialog(
				this.dossier.flowType, dialog, WorkingActions.START_WORKING, () =>
					AssistanceCenterActions.startWorking(
						this.messageManagerService, this.dossiersService.getDossierRefresher(), this.loaderService, this.workingsService, this.selectCommoditiesService, this.dossier, this.working.id, this.allQuotations
					)
				)
			],
			[WorkingActions.MODIFY_QUOTATION, () => CommonActions.editQuotation(
					this.quotationService, this.messageManagerService, this.onEditedQuotationSubject, this.loaderService, this.environment, this.dossier, this.working, this.allQuotations
				)
			],
			[WorkingActions.FINISH_WORKING, () => CommonActions.openConfirmActionDialog(
				this.dossier.flowType, dialog, WorkingActions.FINISH_WORKING, () =>
					AssistanceCenterActions.stopWorking(this.messageManagerService, this.dossiersService.getDossierRefresher(), this.loaderService,
						this.workingsService, this.selectCommoditiesService, this.dossier, this.working.id, this.allQuotations
					)
				)
			],
			[WorkingActions.DELETE_QUOTATION, () => CommonActions.deleteQuotation(
					this.quotationService, this.messageManagerService, this.dossiersService.getDossierRefresher(), this.loaderService, this.dossier, this.working.id, this.allQuotations
				)
			],
			[WorkingActions.EDIT_WARRANTY_TYPE, () => CommonActions.multiChangeWarranty(
					this.dialog, this.tableSelectedEntry, this.loaderService, this.warrantyService, this.dossiersService.getDossierRefresher(), this.messageManagerService, this.dossier.id, this.working.id,
					this.mechanicQuotationDetail ? this.mechanicQuotationDetail.id : this.carBodyQuotationDetail.id
				)
			],
			[WorkingActions.INTEGRATE_QUOTATION, () => AssistanceCenterActions.integrateQuotation(
					this.quotationService, this.messageManagerService, this.onEditedQuotationSubject, this.loaderService, this.environment, this.dossier, this.working, this.allQuotations
				)
			],
			[WorkingActions.ARRIVED, () => AssistanceCenterActions.arrived(
					this.messageManagerService, this.workingsService, this.tableSelectedEntry, this.loaderService, this.dossiersService.getDossierRefresher(), this.dossier, this.working.id, this.allQuotations
				)
			],
			[WorkingActions.RE_APPROVE, () => AuthorityActions.reApproveQuotation(
					this.quotationService, this.loaderService, this.dossier.id, this.working.id, this.allQuotations, this.dossiersService.getDossierRefresher(), this.messageManagerService
				)
			],
			[WorkingActions.RE_DECLINE, () => CommonActions.openDeclineDialog(
					this.dialog, this.dossier.id, this.working, this.allQuotations, this.dossiersService.getDossierRefresher(), WorkingActions.RE_DECLINE
				)
			],
			[WorkingActions.DEALER_MANY_PARTS, () => AuthorityActions.dealerChecked(
					this.dialog, this.tableSelectedEntry, this.dossier.id, this.working, this.allQuotations, this.dossier.flowType,this.assignManyParts = true
				)
			],
			[WorkingActions.EXTRA_TOTAL_CONSIP, () => AuthorityActions.getExtraTotal(
					this.loaderService, this.consipFlowService, this.messageManagerService, this.dossiersService.getDossierRefresher(), this.dossier.id, this.working.id, this.allQuotations, this.tableSelectedEntry
				)
			],
			[WorkingActions.SEND_TO_CONSIP, () => AuthorityActions.sendToConsip(
					this.loaderService, this.consipFlowService, this.messageManagerService, this.dossiersService.getDossierRefresher(), this.dossier.id, this.working.id, this.allQuotations, this.tableEntry
				)
			],
			[WorkingActions.CONSIP_APPROVE, () => AuthorityActions.consipApprove(
					this.dialog, this.loaderService, this.consipFlowService, this.messageManagerService, this.tableEntry, this.dossiersService.getDossierRefresher(), this.dossier.id, this.working.id, this.allQuotations
				)
			],
			[WorkingActions.CONSIP_REJECT, () => CommonActions.openDeclineDialog(
					this.dialog, this.dossier.id, this.working, this.allQuotations, this.dossiersService.getDossierRefresher(), WorkingActions.CONSIP_REJECT
				)
			],
			[WorkingActions.REMOVE_EXTRA_TOTAL_CONSIP, () => AuthorityActions.consipRemoveExtraTotal(
					this.loaderService, this.consipFlowService, this.messageManagerService, this.dossiersService.getDossierRefresher(), this.dossier.id, this.working.id, this.allQuotations, this.tableSelectedEntry, this.tableEntry
				)
			],
			[WorkingActions.SUPER_APPROVE, () => SupervisorActions.superApprove(
					this.quotationService, this.loaderService, this.messageManagerService, this.dossiersService.getDossierRefresher(), this.dossier.id, this.working.id, this.allQuotations
				)
			],
			[WorkingActions.SENT_TO_SUPERVISOR, () => AuthorityActions.sendToSupervisor(
					this.quotationService, this.loaderService, this.dossiersService.getDossierRefresher(), this.messageManagerService,  this.dossier.id, this.working.id, this.allQuotations
				)
			],
			[WorkingActions.SUPER_REJECT, () => CommonActions.openDeclineDialog(
				this.dialog, this.dossier.id, this.working, this.allQuotations, this.dossiersService.getDossierRefresher(), WorkingActions.SUPER_REJECT
				)
			],
			[WorkingActions.SUPER_DECLINE, () => CommonActions.openDeclineDialog(
					this.dialog, this.dossier.id, this.working, this.allQuotations, this.dossiersService.getDossierRefresher(), WorkingActions.SUPER_DECLINE
				)
			],
			[WorkingActions.CANCEL_SUPER_REJECT, () => SupervisorActions.cancelSuperReject(
					this.dossier, this.dialog, this.quotationService, this.loaderService, this.dossiersService.getDossierRefresher(), this.messageManagerService,  this.dossier.id, this.working, this.allQuotations
				)
			],
			[WorkingActions.DOWNLOAD_PDF, () => SupervisorActions.downloadWoePdf(
					this.quotationService, this.messageManagerService, this.dossier, this.working.id, this.allQuotations
				)
			],
			[WorkingActions.SEND_ADMINISTRATION_PRACTICE, () =>
				this.needSendAdministrationPracticeSubject.next(true)
			],
			[WorkingActions.BILL_WITHOUT_LAST_INTEGRATION, () => CommonActions.openConfirmActionDialog(
				null , dialog, WorkingActions.BILL_WITHOUT_LAST_INTEGRATION, () => AssistanceCenterActions.billWithoutLastIntegration(
					this.quotationService, this.loaderService, this.dossiersService.getDossierRefresher(), this.messageManagerService,  this.dossier.id, this.working.id, this.allQuotations
					)
				)
			],
			[WorkingActions.SEND_ALERT, () => CommonActions.createWorkingAlert(
				this.alertService, this.dossier, this.working
				)
			],
			[WorkingActions.FORCE_CHANNELING, () => SupervisorActions.forcedChanneling(
				this.working, this.dialog, this.authorityChannelingService, this.messageManagerService, this.loaderService, this.dossiersService.getDossierRefresher(),
				[{workingId: this.working.id, workingType: this.working.type}], [this.working.type]
				)
			],
			[WorkingActions.REJECT, () => CommonActions.openDeclineDialog(
				this.dialog, this.dossier.id, this.working, this.allQuotations, this.dossiersService.getDossierRefresher(), WorkingActions.REJECT, false, this.dossier
				)
			],
			[WorkingActions.DOWNLOAD_QUOTATION_PDF,
				() => CommonActions.downloadQuotationPdf(
					this.dossier, this.working.id, this.allQuotations.quotations[0].id, this.working.working_number,
					this.dossier.plate, this.documentService, this.loaderService, this.messageManagerService
				)
			],
			[WorkingActions.PREVIEW_QUOTATION_PDF,
				() => AuthorityActions.previewQuotationPdf(
					this.dialog, this.dossier.id, this.working.id, this.allQuotations.quotations[0].id, this.documentService, this.loaderService
				)
			],
			[WorkingActions.FORCE_QUOTATION_REFRESH, 
				() => AuthorityActions.forceQuotationRefresh(
					this.dossier, this.working.id, this.allQuotations.quotations[0].id, this.dossiersService.getDossierRefresher(), this.loaderService, this.quotationService, this.messageManagerService
				)
			]
		]);
		this.assignManyParts = false;
	}

	changeSelectionEntries(entriesSelected: QuotationEntry[]): void {
		this.showItemTableSelectionActions = entriesSelected.length > 0;
		this.tableSelectedEntry = entriesSelected;
	}

	changeInvoiceList(event: InvoiceModel[]): void {
		this.invoiceList = event;
	}
}
